import { useState, useEffect, useCallback, useRef } from 'react'
import { KanbanColumn } from './KanbanColumn'
import { listFeedback, updateStatus, reorderFeedback, type FeedbackItem } from '../../lib/feedbackApi'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'
import type { FeedbackFilterState } from './FeedbackFilters'

interface FeedbackKanbanProps {
  isAdmin: boolean
  onCardClick: (id: number) => void
  refreshKey?: number
  mergeResult?: {
    mergedItem: FeedbackItem
    sourceIds: number[]
    nonce: number
  } | null
  filters?: FeedbackFilterState
  showArchive?: boolean
}

const COLUMNS = [
  { title: 'Backlog', status: 'backlog' },
  { title: 'In Progress', status: 'in_progress' },
  { title: 'Done', status: 'done' },
] as const

export function FeedbackKanban({
  isAdmin,
  onCardClick,
  refreshKey,
  mergeResult,
  filters,
  showArchive,
}: FeedbackKanbanProps) {
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const filtersRef = useRef(filters)
  const showArchiveRef = useRef(showArchive)
  filtersRef.current = filters
  showArchiveRef.current = showArchive

  const fetchItems = useCallback(async () => {
    const f = filtersRef.current
    // Board always uses most_voted sort (which respects sort_order for manual reordering)
    const params: Record<string, unknown> = { per_page: 100, sort: 'most_voted' }
    if (f?.type) params.type = f.type
    if (f?.mine) params.mine = true
    if (f?.search) params.search = f.search
    if (showArchiveRef.current) params.include_archived = true
    // Note: status filter not passed to API — board shows all statuses in columns
    // but we client-filter if a specific status is selected

    try {
      const res = await listFeedback(params)
      setItems(res.items)
      setError(null)
    } catch (err) {
      setError('Failed to load feedback')
      throw err // re-throw for useAutoRefresh backoff
    } finally {
      setLoading(false)
    }
  }, [])

  useAutoRefresh(fetchItems, {
    baseInterval: 30000,
    maxInterval: 120000,
    enabled: true,
  })

  // Re-fetch on refreshKey or filter changes
  useEffect(() => {
    fetchItems()
  }, [refreshKey, fetchItems, filters, showArchive])

  useEffect(() => {
    if (!mergeResult) return
    const sourceSet = new Set(mergeResult.sourceIds)
    setItems((prev) =>
      prev
        .filter((item) => !sourceSet.has(item.id))
        .map((item) => (item.id === mergeResult.mergedItem.id ? mergeResult.mergedItem : item))
    )
  }, [mergeResult])

  const handleDrop = useCallback(async (itemId: number, newStatus: string) => {
    // Find the item to move
    const item = items.find((i) => i.id === itemId)
    if (!item || item.status === newStatus) return

    // Optimistic update
    setItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, status: newStatus } : i))
    )

    try {
      await updateStatus(itemId, newStatus)
    } catch {
      // Revert on error
      setItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, status: item.status } : i))
      )
    }
  }, [items])

  const handleReorder = useCallback(async (_status: string, orderedIds: number[]) => {
    // Optimistic: reorder items locally
    const orderedSet = new Set(orderedIds)
    setItems((prev) => {
      // Split: items in this column (reordered) + items in other columns (unchanged)
      const otherItems = prev.filter((i) => !orderedSet.has(i.id))
      const reorderedItems = orderedIds
        .map((id) => prev.find((i) => i.id === id))
        .filter(Boolean) as FeedbackItem[]
      return [...otherItems, ...reorderedItems]
    })

    try {
      await reorderFeedback(orderedIds)
    } catch {
      // Revert by re-fetching
      fetchItems()
    }
  }, [fetchItems])

  // Group items by status
  const grouped = COLUMNS.map((col) => ({
    ...col,
    items: items.filter((item) => item.status === col.status),
  }))

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  if (error && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        {error}
      </div>
    )
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {grouped.map((col) => (
        <KanbanColumn
          key={col.status}
          title={col.title}
          status={col.status}
          items={col.items}
          isAdmin={isAdmin}
          onDrop={handleDrop}
          onReorder={handleReorder}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  )
}
